import React, { useState, useEffect } from 'react';
import { ref, get, child } from 'firebase/database';
import { database } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import Sidebar from "../Sidebar/SidebarAdmin";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Avatar,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Fingerprint as FingerprintIcon,
  CalendarToday as CalendarIcon,
  Badge as BadgeIcon
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const customTheme = createTheme({
  palette: {
    primary: { main: '#14A3C7', contrastText: '#ffffff' },
    secondary: { main: '#EC6D71', contrastText: '#ffffff' },
    background: { default: '#F8F8F8' },
    text: { primary: '#333333', secondary: '#666666' }
  },
  typography: { fontFamily: 'Montserrat, sans-serif' }
});

const AdminDisplay = () => {
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);
  const sidebarWidth = isSidebarOpen ? 250 : 80;

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        const currentUser = JSON.parse(localStorage.getItem('currentUser'));
        
        if (!currentUser || currentUser.type !== 'admin') {
          navigate('/login');
          return;
        }

        const dbRef = ref(database);
        const snapshot = await get(child(dbRef, 'admins'));
        
        if (snapshot.exists()) {
          const admins = snapshot.val();
          const adminEntry = Object.entries(admins).find(
            ([_, admin]) => admin.name === currentUser.name
          );
          
          if (adminEntry) {
            setAdminData({
              id: adminEntry[0],
              ...adminEntry[1]
            });
          } else {
            setError('Admin data not found');
          }
        }
      } catch (err) {
        setError('Error fetching admin data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    navigate('/login');
  };

  if (loading) {
    return (
      <ThemeProvider theme={customTheme}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh"
          }}
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={customTheme}>
      <div style={{ display: "flex" }}>
        <Sidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

        <Box
          sx={{
            marginLeft: isMobile ? -2 : `${sidebarWidth + 10}px`,
            width: isMobile ? "100%" : `calc(100% - ${sidebarWidth}px)`,
            transition: "margin-left 0.3s ease",
            padding: "20px",
            backgroundColor: "#f4f4f4",
            minHeight: "100vh",
            paddingTop: "70px",
            marginTop: isMobile ? "10px" : "-40px",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 4
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: "bold", color: "#0099B0" }}>
              Admin Profile
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </Box>

          {error ? (
            <Card sx={{ p: 2, backgroundColor: "#ffebee" }}>
              <Typography color="error">{error}</Typography>
            </Card>
          ) : (
            <Card
              elevation={3}
              sx={{
                maxWidth: 800,
                margin: "0 auto",
                borderRadius: 2,
                overflow: "hidden"
              }}
            >
              <Box
                sx={{
                  backgroundColor: "primary.main",
                  p: 3,
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  gap: 2
                }}
              >
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: "white",
                    color: "primary.main"
                  }}
                >
                  {adminData?.name?.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    {adminData?.name}
                  </Typography>
                  <Typography variant="subtitle1">
                    Administrator
                  </Typography>
                </Box>
              </Box>

              <CardContent>
                <Stack spacing={3}>
                  <Box>
                    <Typography
                      variant="h6"
                      color="primary"
                      sx={{ mb: 2, fontWeight: "bold" }}
                    >
                      Account Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Stack spacing={2}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <BadgeIcon color="primary" />
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Admin ID
                          </Typography>
                          <Typography variant="body1">
                            {adminData?.id}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <EmailIcon color="primary" />
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Email Address
                          </Typography>
                          <Typography variant="body1">
                            {adminData?.email || "Not provided"}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <FingerprintIcon color="primary" />
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Fingerprint ID
                          </Typography>
                          <Typography variant="body1">
                            {adminData?.fingerprint_id}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CalendarIcon color="primary" />
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Registration Date
                          </Typography>
                          <Typography variant="body1">
                            {adminData?.registration_date}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Box>
      </div>
    </ThemeProvider>
  );
};

export default AdminDisplay;